import React from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import { ArrowLeft, Calendar, Clock3, MessageCircle, Server, Shield } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import Header from "../components/Header";
import SEO from "../components/SEO";
import { BLOG_POSTS } from "../mock/blog";
import { WHATSAPP_LINK } from "../config/contact";

function toIsoDate(humanDate) {
  const parsed = new Date(humanDate);
  if (Number.isNaN(parsed.getTime())) {
    return new Date().toISOString();
  }
  return parsed.toISOString();
}

export default function BlogPost() {
  const { slug } = useParams();
  const post = BLOG_POSTS.find((item) => item.slug === slug);

  if (!post) {
    return (
      <>
        <SEO
          title="Artículo no encontrado | WPRescate24"
          description="El artículo solicitado no está disponible."
          robots="noindex,nofollow"
          canonicalUrl="https://wprescate24.com/blog"
        />
        <div className="dark min-h-screen bg-background">
          <Header currentPage="blog" />
          <main className="max-w-4xl mx-auto px-6 py-20 text-center">
            <h1 className="text-3xl font-bold text-foreground">Artículo no encontrado</h1>
            <p className="mt-3 text-foreground/70">Revisa el listado de contenidos recientes.</p>
            <Button asChild className="mt-8 bg-primary hover:bg-primary/90 text-white">
              <RouterLink to="/blog">Volver al blog</RouterLink>
            </Button>
          </main>
        </div>
      </>
    );
  }

  const canonicalUrl = `https://wprescate24.com/blog/${post.slug}`;
  const postKeywords = post.seoKeywords || post.keywords || "";
  const blogPostingSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.seoDescription,
    "datePublished": toIsoDate(post.date),
    "dateModified": toIsoDate(post.date),
    "mainEntityOfPage": canonicalUrl,
    "author": {
      "@type": "Organization",
      "name": "WPRescate24"
    },
    "publisher": {
      "@type": "Organization",
      "name": "WPRescate24"
    },
    "articleSection": post.category,
    "keywords": postKeywords
  };

  return (
    <>
      <SEO
        title={`${post.title} | Blog WPRescate24`}
        description={post.seoDescription}
        keywords={postKeywords}
        canonicalUrl={canonicalUrl}
        structuredData={blogPostingSchema}
      />
      <div className="dark min-h-screen bg-background">
        <Header currentPage="blog" />
        <main className="max-w-4xl mx-auto px-6 py-16 md:py-20">
          <RouterLink to="/blog" className="inline-flex items-center gap-2 text-sm text-primary hover:opacity-80 mb-8">
            <ArrowLeft className="w-4 h-4" />
            Volver al blog
          </RouterLink>

          <article>
            <div className="mb-8">
              <p className="text-sm uppercase tracking-wide text-secondary font-semibold">{post.category}</p>
              <h1 className="mt-3 text-4xl md:text-5xl font-bold text-foreground leading-tight">{post.title}</h1>
              <div className="mt-5 flex flex-wrap items-center gap-5 text-sm text-foreground/70">
                <span className="inline-flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  {post.date}
                </span>
                <span className="inline-flex items-center gap-2">
                  <Clock3 className="w-4 h-4" />
                  {post.readTime}
                </span>
              </div>
            </div>

            <div className="prose prose-invert max-w-none prose-p:text-foreground/85 prose-headings:text-foreground">
              {post.sections?.map((section) => (
                <section key={section.heading} className="mb-10">
                  <h2 className="text-2xl md:text-3xl font-bold mb-4">{section.heading}</h2>
                  {Array.isArray(section.content) ? (
                    <div className="space-y-4">
                      {section.content.map((paragraph, index) => (
                        <p key={`${section.heading}-${index}`} className="text-lg leading-relaxed">{paragraph}</p>
                      ))}
                    </div>
                  ) : (
                    <p className="text-lg leading-relaxed">{section.content}</p>
                  )}
                </section>
              ))}
            </div>

            <Card className="mt-12 border-2 border-secondary/40 bg-secondary/5">
              <CardContent className="p-7">
                <h3 className="text-2xl font-bold text-foreground">¿Quieres aplicar esto en tu sitio hoy?</h3>
                <p className="mt-3 text-foreground/80">
                  Escríbenos por WhatsApp y revisamos tu caso con acciones inmediatas de seguridad, rendimiento y SEO técnico.
                </p>
                <Button className="mt-6 bg-secondary hover:bg-secondary/90 text-white font-bold" asChild>
                  <a href={WHATSAPP_LINK} target="_blank" rel="noopener">
                    <MessageCircle className="w-4 h-4 mr-2" />
                    Hablar con un especialista
                  </a>
                </Button>
              </CardContent>
            </Card>
          </article>
        </main>

        <footer className="bg-primary text-white">
          <div className="max-w-7xl mx-auto px-6 py-10">
            <div className="grid md:grid-cols-3 gap-8 mb-8">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Shield className="w-6 h-6 text-[#b8cd37]" />
                  <span className="font-bold text-white text-lg">WPRescate24</span>
                </div>
                <p className="text-sm text-white/70">
                  Agencia especializada en: optimización, rescate, seguridad y blindaje de WordPress y sitios web.
                </p>
              </div>
              <div>
                <h4 className="font-bold text-white mb-3">Servicios</h4>
                <div className="flex flex-col gap-2">
                  <RouterLink to="/" className="text-sm text-white/70 hover:text-white transition-colors">Rescate WordPress</RouterLink>
                  <RouterLink to="/mantenimiento-wordpress" className="text-sm text-white/70 hover:text-white transition-colors">Mantenimiento Proactivo</RouterLink>
                  <RouterLink to="/hosting-wordpress" className="text-sm text-white/70 hover:text-white transition-colors">Hosting WordPress</RouterLink>
                  <RouterLink to="/blog" className="text-sm text-white/70 hover:text-white transition-colors">Blog</RouterLink>
                </div>
              </div>
              <div>
                <h4 className="font-bold text-white mb-3">Infraestructura</h4>
                <p className="text-sm text-white/70 mb-2">Nuestros servicios están respaldados por:</p>
                <a
                  href="https://servidores.garey.mx"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-[#b8cd37] hover:text-[#c6da45] transition-colors"
                >
                  <Server className="w-4 h-4" />
                  servidores.garey.mx
                </a>
              </div>
            </div>
            <div className="border-t border-white/15 pt-6 flex flex-col md:flex-row items-center justify-between text-sm text-white/60">
              <div>© {new Date().getFullYear()} WPRescate24. Todos los derechos reservados. Una empresa de <a href="https://garey.mx" target="_blank" rel="noopener noreferrer" className="text-white hover:underline font-semibold">GAREY AW</a></div>
              <div className="flex items-center gap-6 mt-3 md:mt-0">
                <RouterLink to="/terminos" className="hover:text-white transition-colors">Términos</RouterLink>
                <RouterLink to="/privacidad" className="hover:text-white transition-colors">Privacidad</RouterLink>
                <a href={WHATSAPP_LINK} target="_blank" rel="noopener" className="hover:text-[#b8cd37] transition-colors">WhatsApp</a>
              </div>
            </div>
          </div>
        </footer>

        <a href={WHATSAPP_LINK} target="_blank" rel="noopener" aria-label="Abrir WhatsApp" className="fixed bottom-6 right-6 z-50 group">
          <div className="h-16 w-16 rounded-full bg-[#25D366] hover:bg-[#20bd5a] flex items-center justify-center shadow-2xl border-2 border-white/30 transition-all hover:scale-110">
            <MessageCircle className="w-8 h-8 text-white" />
          </div>
        </a>
      </div>
    </>
  );
}
